import { Size, Variant } from '../../types';
import { ExecutionContext } from 'styled-components';

export type CommonButtonProps = {
    variant?: Variant;
    disabled?: boolean;
    loading?: boolean;
    size?: Size;
};

function borderWidthPx(theme: ExecutionContext['theme'], size: Size = Size.NORMAL) {
    return theme.componentSizes.button[size].border;
}

export const IGNORE_BUTTON_PROPS = [
    'variant',
    'loading',
    'size',
    'padding',
    'fillParent',
    'showInPrint',
];

export function buttonBackgroundColor(props: CommonButtonProps & ExecutionContext) {
    const { variant, disabled, loading, theme } = props;

    if (disabled || loading) {
        return theme.buttonColorDisabled;
    }

    switch (variant) {
        case Variant.PRIMARY:
            return theme.componentColors.button.primary.bgDefault;
        case Variant.PRIMARY_ALTERNATIVE:
            return theme.componentColors.button.primary.bgAlternativeDefault;
        case Variant.SECONDARY:
        case Variant.SECONDARY_ALTERNATIVE:
            return 'transparent';
        case Variant.ATTENTION:
            return theme.primitiveColors.urbanOrange[200];
        case Variant.TERTIARY:
        case Variant.TEXT:
        case Variant.A11Y:
            return 'transparent';
        case Variant.IMPORTANT:
            return theme.componentColors.button.important.bgImportant;
        case Variant.GRAYSCALE:
            return theme.background.alternative;
        default:
            return theme.buttonColorSecondary;
    }
}

export function buttonColor(props: CommonButtonProps & ExecutionContext) {
    const { variant, disabled, loading, theme } = props;

    if (disabled) {
        return theme.buttonTextColorDisabled;
    }
    if (loading) {
        return theme.buttonTextColorSecondary;
    }
    switch (variant) {
        case Variant.PRIMARY:
            return theme.componentColors.button.primary.fgDefault;
        case Variant.PRIMARY_ALTERNATIVE:
            return theme.componentColors.button.primary.fgAlternativeDefault;
        case Variant.SECONDARY:
            return theme.componentColors.button.secondary.fgDefault;
        case Variant.SECONDARY_ALTERNATIVE:
            return theme.componentColors.button.secondary.fgAlternativeDefault;
        case Variant.IMPORTANT:
            return theme.componentColors.button.important.fgImportant;
        default:
            return theme.foreground.default;
    }
}

/**
 * Returns a complete CSS `border` value (e.g. `'2px solid #43434A'` or `'none'`).
 * Width comes from `theme.componentSizes.button[size].border` so it scales with
 * the size variant.
 */
export function buttonBorder(props: CommonButtonProps & ExecutionContext) {
    const { variant, size, theme } = props;
    const width = borderWidthPx(theme, size);

    switch (variant) {
        case Variant.SECONDARY:
            return `${width}px solid ${theme.componentColors.button.secondary.borderDefault}`;
        case Variant.SECONDARY_ALTERNATIVE:
            return `${width}px solid ${theme.border.light}`;
        default:
            return 'none';
    }
}
